import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from 'src/environments/environment';

export interface AccountIcon {
  id: number;
  accountId: number;
  createdAt: string;
  createdBy: number;
  s3Key: string;
  url: string;
}

export interface UploadedAccountIcon extends AccountIcon {
  processed: boolean;
  width: number;
  height: number;
  size: number;
  queueMessage?: string;
}

/** Учётку библиотеки передаёт только CMS: в мониторинге бэкенд берёт её из пользователя */
const isCms = environment.systemType === 'cms';

export const ACCOUNT_ICON_MAX_FILE_SIZE = 4 * 1024 * 1024;

export const ACCOUNT_ICON_MAX_FILES = 1;

export const ACCOUNT_ICON_EXTENSIONS = [
  'png',
  'jpg',
  'jpeg',
  'bmp',
  'webp',
  'svg',
  'heic',
  'heif',
] as const;

export const getAccountIconErrorMessage = (error: unknown): string | null => {
  const body = error instanceof HttpErrorResponse ? error.error : null;
  const detail = body?.detail ?? body?.file ?? body?.files ?? body?.error;

  if (Array.isArray(detail)) {
    return detail.join(' ') || null;
  }

  return typeof detail === 'string' && detail ? detail : null;
};

@Injectable({ providedIn: 'root' })
export class AccountIconsService {
  private readonly httpClient = inject(HttpClient);

  list(accountId: number | null): Observable<AccountIcon[]> {
    return this.httpClient.get<AccountIcon[]>('account_icons/', {
      params: this.accountParams(accountId),
    });
  }

  upload(file: File, accountId: number | null): Observable<UploadedAccountIcon> {
    const body = new FormData();
    body.append('file', file, file.name);
    if (isCms && accountId) {
      body.append('account_id', String(accountId));
    }

    return this.httpClient.post<UploadedAccountIcon>('account_icons/', body);
  }

  delete(iconId: number, accountId: number | null): Observable<void> {
    return this.httpClient.delete<void>(`account_icons/${iconId}/`, {
      params: this.accountParams(accountId),
    });
  }

  private accountParams(accountId: number | null): HttpParams {
    return isCms && accountId
      ? new HttpParams().set('cms_account_id', accountId)
      : new HttpParams();
  }
}
